/**
 * TPRA Risk Heatmap API Request Hook
 * Fetches risk heatmap matrix data for dashboard
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { RiskHeatmapResponse } from "../types";
import { RiskHeatmapResponseSchema } from "../validation/schemas";

/**
 * Fetches risk heatmap data from the API
 * @returns Validated risk heatmap response
 * @throws Error if API request fails or response validation fails
 */
async function fetchRiskHeatmap(): Promise<RiskHeatmapResponse> {
  const response = await get<RiskHeatmapResponse>({
    url: APIEndpoints.TPRAHomeRiskHeatmap,
  });

  // Validate response with Zod schema
  const validated = RiskHeatmapResponseSchema.parse(response);

  return validated;
}

/**
 * React Query hook for fetching TPRA risk heatmap
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 *
 * @returns React Query result with risk heatmap data
 *
 * @example
 * ```typescript
 * function RiskHeatmap() {
 *   const { data, isLoading, isError, refetch } = useGetRiskHeatmap();
 *
 *   if (isLoading) return <HeatmapSkeleton />;
 *   if (isError) return <SectionError onRetry={refetch} />;
 *
 *   return <HeatmapGrid matrix={data.matrix} />;
 * }
 * ```
 */
export function useGetRiskHeatmap(): UseQueryResult<RiskHeatmapResponse, Error> {
  return useQuery({
    queryKey: tpraHomeQueryKeys.heatmap(),
    queryFn: fetchRiskHeatmap,

    // Caching Strategy
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention

    // Retry Strategy (exponential backoff)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

    // Refetch Behavior
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
